// React/Gatsby
import { BoxProps, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, Form, FormikProvider, useFormik } from "formik";
import React from "react";
import AppBox from "../components/AppBox/AppBox";
import BackgroundBox from "../components/common/Box/Background";
import Box from "../components/common/Box/Box";
import Button from "../components/common/Button";
import FullscreenContainer from "../components/common/Container/FullScreen";
import Checkbox from "../components/common/Input/Checkbox";
import FormControl from "../components/common/Input/FormControl";
import Input from "../components/common/Input/Input";
import Textarea from "../components/common/Input/Textarea";
import Link from "../components/common/Link/Link";
import Modal from "../components/common/Modal/Modal";
import { SplashScreen } from "../components/common/SplashScreen";
import Text from "../components/common/Text/Text";
import { AuthProvider } from "../providers/AuthProvider";
import UserProvider from "../providers/UserProvider";
import { getErrorMessage } from "../store/api";
import validator from "validator";
import { useGetRegionQuery } from "../store/api/client";
import { useCreateFeedbackMutation } from "../store/api/feedback";
import { TurnstileProvider, useTurnstileContext } from "../providers/TurnstileProvider";
import { NewsletterProvider, useNewsletter } from "../providers/NewsletterProvider";
import NewsletterModal from "../components/common/Modal/Newsletter";
import "./app.css";

export const Background = () => {
  const region = useGetRegionQuery({});

  const background = React.useMemo(() => {
    const background = {
      image: "",
      link: "",
    };

    if (region.data) {
      const chance = Math.random();

      if (chance > 0.5) {
        background.link = "https://max.mailbigfile.com/register/?discount=BIGSALE50";

        if (region.data.country.iso_code === "GB") {
          background.image =
            "https://d713p78z0ghru.cloudfront.net/free/free-backgrounds/ProAccount01.jpg";
        } else if (region.data.continent.code === "EU") {
          background.image =
            "https://d713p78z0ghru.cloudfront.net/free/free-backgrounds/ProAccount03.jpg";
        } else {
          background.image =
            "https://d713p78z0ghru.cloudfront.net/free/free-backgrounds/ProAccount02.jpg";
        }
      } else {
        background.image =
          "https://d713p78z0ghru.cloudfront.net/free/free-backgrounds/30-day-free.jpg";
        background.link =
          "https://www.mailbigfile.com/business/register-free-trial-account/";
      }
    }

    if (region.isError) {
      background.image =
        "https://d713p78z0ghru.cloudfront.net/free/free-backgrounds/30-day-free.jpg";
      background.link =
        "https://www.mailbigfile.com/business/register-free-trial-account/";
    }

    return background;
  }, [region.data]);

  return (
    <Link to={background.link} target="_blank" position="relative">
      <BackgroundBox background={background.image} />
    </Link>
  );
};

const FeedbackModal = ({ onClose }: { onClose: () => void }) => {
  const [createFeedback] = useCreateFeedbackMutation();

  const toast = useToast();

  const { consume } = useTurnstileContext();

  const formik = useFormik({
    initialValues: {
      email: "",
      message: "",
      contact: false,
    },
    async onSubmit(values, actions) {
      try {
        const token = consume();

        let recaptcha = undefined

        if (token) {
          recaptcha = {
            token,
            action: "feedback",
            type: "turnstile",
          };
        }

        const response = await createFeedback({
          body: {
            message: values.message,
            email: values.email,
            contact: values.contact,
          },
          params: {
            recaptcha
          }
        });

        if ("error" in response) {
          throw new Error(getErrorMessage(response.error));
        }

        toast({
          title: "Success",
          description: "Thanks for your feedback",
          status: "success",
        });

        onClose();
      } catch (e: any) {
        if (e instanceof Error) {
          if (typeof e.cause === "string") {
            actions.setFieldError(e.cause, e.message);
          } else {
            toast({
              title: "An error occurred",
              description: e.message,
              status: "error",
            });
          }
        }
      } finally {
        actions.setSubmitting(false);
        actions.resetForm();
      }
    },
  });

  return (
    <Modal isOpen={true} onClose={onClose}>
      <Modal.Overlay />

      <FormikProvider value={formik}>
        <Form style={{ display: "contents" }}>
          <Modal.Content m="4">
            <Modal.Header>Feedback</Modal.Header>

            <Modal.CloseButton />

            <Modal.Body display="flex" flexDir="column" gap="4">
              <FormControl variant="floating" helper="Optional">
                <Field
                  as={Input}
                  id="email"
                  name="email"
                  type="email"
                  variant="filled"
                  label="Email"
                  placeholder=" "
                  validate={async (value: string) => {
                    if (!value) {
                      return "Please provide your email address";
                    }

                    if (!validator.isEmail(value)) {
                      return "Invalid email address";
                    }
                  }}
                />
              </FormControl>

              <FormControl
                variant="floating"
                isInvalid={
                  !!(formik.touched["message"] && formik.errors["message"])
                }
                helper={`Remaining characters: ${500 - formik.values.message.length}`}
                error={
                  <Text color={"crimson"}>{formik.errors["message"]}</Text>
                }
              >
                <Field
                  as={Textarea}
                  id="message"
                  name="message"
                  type="message"
                  variant="filled"
                  label="Message"
                  placeholder=" "
                  validate={(value: string) => {
                    if (!value) {
                      return "Required";
                    }

                    if (value.length > 500) {
                      return "Message length must not exceed 500";
                    }
                  }}
                />
              </FormControl>

              <FormControl>
                <Field as={Checkbox} name="contact" spacing="4" pl="2">
                  I do not wish to be contacted regarding my feedback.
                </Field>
              </FormControl>
            </Modal.Body>

            <Modal.Footer display="flex" gap="4">
              <Button type="submit" isProcessing={formik.isSubmitting}>
                Submit
              </Button>

              <Button onClick={onClose} isProcessing={formik.isSubmitting}>
                Close
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Form>
      </FormikProvider>
    </Modal>
  );
}

const FeedbackButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Box
        display="flex"
        flexDir="column"
        pos="fixed"
        bottom="4"
        right="4"
        padding="3"
        borderRadius="lg"
        textAlign="center"
        background="linear-gradient(45deg, #625fa2 0%, #e358b4 50%, #625fa2 100%)"
        backgroundSize="200% 100%"
        backgroundPosition="left center"
        color="white"
        transition="background-position ease-in-out 0.25s"
        cursor="pointer"
        _hover={{ backgroundPosition: "right center" }}
        onClick={onOpen}
      >
        <Text as="span">Tell us about your experience</Text>
      </Box>

      {isOpen && <FeedbackModal onClose={onClose} />}
    </>
  );
};

const NewsletterButton = () => {
  const { setNewsletterOpen } = useNewsletter();

  return (
    <Box
      display="flex"
      flexDir="column"
      pos="fixed"
      bottom="4"
      left="4"
      padding="3"
      borderRadius="lg"
      textAlign="center"
      background="linear-gradient(45deg, #e358b4 0%, #625fa2 50%, #e358b4 100%)"
      backgroundSize="200% 100%"
      backgroundPosition="left center"
      color="white"
      transition="background-position ease-in-out 0.25s"
      cursor="pointer"
      _hover={{ backgroundPosition: "right center" }}
      onClick={() => setNewsletterOpen(true)}
    >
      <Text as="span">Subscribe to our Newsletter</Text>
    </Box>
  );
};

const NewsletterModalWrapper = () => {
  const { newsletterOpen, setNewsletterOpen } = useNewsletter();

  return newsletterOpen ? (
    <NewsletterModal onClose={() => setNewsletterOpen(false)} />
  ) : null;
};

const AppLayout = ({
  appBoxProps,
  children,
}: {
  appBoxProps?: BoxProps;
  children: React.ReactNode;
}) => {
  if (typeof window === "undefined") {
    return <SplashScreen />;
  }

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (
      process.env.GATSBY_ENABLE_ERUDA_DEVTOOLS &&
      params.get("devtools") === "true"
    ) {
      import("eruda").then((eruda) => eruda.default.init({
        useShadowDom: true,
      }));
    }
  }, []);

  return (
    <AuthProvider>
      <UserProvider>
        <TurnstileProvider>
          <NewsletterProvider>
            <FullscreenContainer id="app">
              <Background />

              <AppBox {...appBoxProps}>{children}</AppBox>

              {typeof window !== undefined && window.innerWidth > 450 && (
                <>
                  <NewsletterButton />
                  <FeedbackButton />
                </>
              )}

              <NewsletterModalWrapper />
            </FullscreenContainer>
          </NewsletterProvider>
        </TurnstileProvider>
      </UserProvider>
    </AuthProvider>
  );
};

export default AppLayout;
